import type { Interview } from "@/types";
import { InterviewCard } from "../card/InterviewCard";

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface Props {
  currentDate: Date;
  interviews: Interview[];
  onInterviewClick: (iv: Interview) => void;
  onSlotClick: (date: Date) => void;
  onInterviewDrop: (id: number, newDate: Date) => void;
}

function gridDays(date: Date): Date[] {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return d;
  });
}

export function MonthView({ currentDate, interviews, onInterviewClick, onSlotClick, onInterviewDrop }: Props) {
  const days = gridDays(currentDate);
  const today = new Date().toDateString();
  const month = currentDate.getMonth();

  const byDate = interviews.reduce<Record<string, Interview[]>>((acc, iv) => {
    const key = new Date(iv.scheduled_at).toDateString();
    (acc[key] ??= []).push(iv);
    return acc;
  }, {});

  function handleDrop(e: React.DragEvent<HTMLDivElement>, _day: Interview[], dropDay: Date) {
    e.preventDefault();
    const id = Number(e.dataTransfer.getData("interview-id"));
    if (!id) return;
    const orig = interviews.find((iv) => iv.id === id);
    const d = new Date(dropDay);
    if (orig) {
      const src = new Date(orig.scheduled_at);
      d.setHours(src.getHours(), src.getMinutes(), 0, 0);
    }
    onInterviewDrop(id, d);
  }

  return (
    <div className="flex flex-1 flex-col overflow-auto">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-border-card bg-surface-secondary/50">
        {DAYS_OF_WEEK.map((d) => (
          <div key={d} className="py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
            {d}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="grid flex-1 grid-cols-7">
        {days.map((day, i) => {
          const key = day.toDateString();
          const isToday = key === today;
          const isCurMonth = day.getMonth() === month;
          const ivs = byDate[key] ?? [];
          const [shown, extra] = [ivs.slice(0, 3), ivs.slice(3)];

          return (
            <div
              key={i}
              className={`min-h-[96px] cursor-pointer border-b border-r border-border-card p-1.5 transition-colors
                hover:bg-surface-secondary/40 ${isCurMonth ? "bg-white" : "bg-surface-secondary/20"}`}
              onClick={() => onSlotClick(day)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDrop={(e) => handleDrop(e, ivs, day)}
            >
              {/* Date number */}
              <div className="mb-1 flex justify-end">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold
                  ${isToday ? "bg-rausch text-white" : isCurMonth ? "text-near-black" : "text-text-secondary"}`}>
                  {day.getDate()}
                </span>
              </div>

              {/* Interview cards */}
              <div className="space-y-0.5">
                {shown.map((iv) => (
                  <InterviewCard
                    key={iv.id}
                    interview={iv}
                    compact
                    onClick={(e) => { e.stopPropagation(); onInterviewClick(iv); }}
                  />
                ))}
                {extra.length > 0 && (
                  <p className="pl-0.5 text-[10px] text-text-secondary">+{extra.length} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
